/**
 * PrintTemplateBuilder.tsx — corp's Print Template Builder, for one account:
 * a near-full-screen dialog with
 *   - the header: title, the change summary ("settings modified · 2 sections
 *     modified · order changed") with Revert, the errors / suggestions badges
 *     (hover for the list), Preview and Create / Save;
 *   - left, Template Settings: name, Display Logo, Offered For;
 *   - centre, the 19 sections in print order, each drawn with sample data;
 *     drag a card (or its grip, with the keyboard) to move it, click one to
 *     edit it;
 *   - right, Section Properties: the chosen section's Display switch and what
 *     it prints (switches, the discount display, the disclosure).
 * Every switch has its own "modified" pill; a card's pill reverts the section.
 * Creating keeps the builder open on the new template, as corp does. Closing
 * with changes asks first. Corp drew every card as a receipt ("Receipt total"
 * on a quote-only template); here a card uses the template's first document,
 * and the receipts-only blocks show dimmed.
 *
 * On a Locked Admin page (useAdminPageLevel) it only shows the template, and
 * says nothing about why (R18): titled "Print Template", every field and
 * switch shows its value but doesn't change (a locked FieldLockScope), no
 * drag or grips, no Save / Revert / errors / suggestions, and no wording that
 * invites a change. Picking a section and Preview still work; Close closes.
 */
import { useMemo, useState, type KeyboardEvent, type KeyboardEventHandler, type PointerEventHandler, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle, Eye, EyeOff, GripVertical, RotateCcw, Save, Settings } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FieldLockScope } from '@/components/ui/FieldLock';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  DISCOUNT_DISPLAY_OPTIONS,
  PRINT_HEADER_LABELS,
  PRINT_SECTION_BY_KEY,
  createPrintTemplate,
  flagsOf,
  formFromTemplate,
  newPrintTemplateForm,
  offeredDocTypes,
  printTemplateChecks,
  sectionFormKeys,
  updatePrintTemplate,
  type PrintBoolField,
  type PrintDisclosure,
  type PrintOrderField,
  type PrintSection,
  type PrintTemplate,
  type PrintTemplateForm,
} from '@/services/printTemplatesAdmin.service';
import { toast } from '@/store/toastStore';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { OutlineBadge } from './CorpBadges';
import { DESCRIPTION_CLASS, DialogField, fieldClasses } from './DialogChrome';
import { PrintTemplatePreviewModal, renderPrintSection, type PrintSheetInput } from './PrintTemplateSheet';
import { useTrackedForm } from './useTrackedForm';

const SETTINGS_KEYS: (keyof PrintTemplateForm)[] = ['name', 'logoDisplay', 'forQuotes', 'forReceipts', 'forMemberships'];
const OFFERED_KEYS = ['forQuotes', 'forReceipts', 'forMemberships'] as const;

export interface PrintTemplateBuilderProps {
  /** Null to create one. */
  template: PrintTemplate | null;
  /** The account's templates (for the duplicate-name suggestion). */
  templates: PrintTemplate[];
  disclosures: PrintDisclosure[];
  onClose: () => void;
  /** After each create / save; the builder stays open. */
  onSaved: (saved: PrintTemplate, isNew: boolean) => void;
}

export function PrintTemplateBuilder({ template, templates, disclosures, onClose, onSaved }: PrintTemplateBuilderProps) {
  // The template being edited; set by a create, so the builder carries on in edit mode.
  const [current, setCurrent] = useState<PrintTemplate | null>(template);
  const isNew = current === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<PrintTemplateForm>(() => (template ? formFromTemplate(template) : newPrintTemplateForm()), {
    tracking: !isNew,
  });
  const { form } = t;
  const [selectedKey, setSelectedKey] = useState<PrintOrderField | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [saving, setSaving] = useState(false);

  const { errors, suggestions } = printTemplateChecks(form, templates, current?.quotePrintTemplateID ?? null);

  // The disclosure's name: an active one by id, else the name saved with the template (an inactive one).
  const disclosureName =
    disclosures.find((d) => d.disclosuresID === form.disclosureType)?.name ??
    (current && form.disclosureType === current.disclosureType ? current.disclosureName ?? '' : '');

  const setFlag = (key: PrintBoolField, on: boolean) =>
    // Legacy rule (the API enforces it too): no witness line without a signature line.
    t.setForm((p) => ({ ...p, [key]: on, ...(key === 'signatureLine' && !on ? { witnessSignature: false } : {}) }));

  // ── Change summary ────────────────────────────────────────────────────────────
  const modifiedSections = useMemo(
    () => new Set(form.sequence.filter((k) => sectionFormKeys(PRINT_SECTION_BY_KEY[k]).some((f) => t.changed.has(f)))),
    [form.sequence, t.changed],
  );
  const summary = [
    SETTINGS_KEYS.some((k) => t.changed.has(k)) && 'settings modified',
    modifiedSections.size > 0 && `${modifiedSections.size} section${modifiedSections.size === 1 ? '' : 's'} modified`,
    t.changed.has('sequence') && 'order changed',
  ]
    .filter(Boolean)
    .join(' · ');

  function revertSection(section: PrintSection) {
    for (const key of sectionFormKeys(section)) if (t.changed.has(key)) t.revert(key);
  }

  // ── Drag to reorder ───────────────────────────────────────────────────────────
  const sensors = useSensors(
    // A click (no movement) selects the card; a drag of 5px moves it.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (locked || !over || active.id === over.id) return;
    const from = form.sequence.indexOf(active.id as PrintOrderField);
    const to = form.sequence.indexOf(over.id as PrintOrderField);
    if (from < 0 || to < 0) return;
    t.patch({ sequence: arrayMove(form.sequence, from, to) });
  }

  // ── Close / save ──────────────────────────────────────────────────────────────
  function requestClose() {
    if (saving) return;
    if (t.dirty) setConfirmClose(true);
    else onClose();
  }

  async function handleSave() {
    if (locked || errors.length > 0 || saving) return;
    setSaving(true);
    try {
      const saved = current ? await updatePrintTemplate(current.quotePrintTemplateID, form) : await createPrintTemplate(form);
      const wasNew = current === null;
      setCurrent(saved);
      t.reset(formFromTemplate(saved));
      onSaved(saved, wasNew);
      toast.success(wasNew ? 'Print template created' : 'Print template saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  }

  const flags = flagsOf(form);
  // The cards show the first document the template is offered for (corp drew them all as receipts).
  const docType = offeredDocTypes(flags)[0];
  const sheetInput: PrintSheetInput = {
    name: form.name,
    flags,
    sequence: form.sequence,
    discountDisplayType: form.discountDisplayType,
    disclosureName,
  };
  const selectedSection = selectedKey ? PRINT_SECTION_BY_KEY[selectedKey] : null;
  const verb = isNew ? 'creating' : 'saving';

  const header = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pr-2">
      <div className="min-w-0">
        <h2 className="text-lg font-semibold leading-tight text-slate-900">
          {locked ? 'Print Template' : isNew ? 'New Print Template' : 'Print Template Builder'}
        </h2>
        <p className="truncate text-sm text-slate-500">{form.name.trim() || 'Untitled template'}</p>
      </div>
      {!locked && !isNew && t.dirty && (
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-blue-700" aria-hidden />
          <span className="text-sm text-blue-700">{summary}</span>
          <Button
            variant="ghost"
            size="xs"
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            aria-label="Revert all changes"
            onClick={t.revertAll}
          >
            Revert
          </Button>
        </div>
      )}
      {!locked && errors.length > 0 && (
        <HoverList tone="error" label={`${errors.length} ${errors.length === 1 ? 'error' : 'errors'}`} heading={`Required before ${verb}:`} items={errors} />
      )}
      {!locked && suggestions.length > 0 && (
        <HoverList
          tone="warning"
          label={`${suggestions.length} ${suggestions.length === 1 ? 'suggestion' : 'suggestions'}`}
          heading={`Suggestions before ${verb}:`}
          items={suggestions}
        />
      )}
      <div className="ml-auto flex items-center gap-2">
        <Button variant="secondary" size="sm" leftIcon={<Eye className="h-4 w-4" />} onClick={() => setPreviewOpen(true)}>
          Preview
        </Button>
        {locked ? (
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        ) : (
          <Button
            size="sm"
            leftIcon={<Save className="h-4 w-4" />}
            loading={saving}
            disabled={errors.length > 0 || saving || (!isNew && !t.dirty)}
            onClick={() => void handleSave()}
          >
            {isNew ? 'Create' : 'Save'}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={requestClose}
      closeOnEscape={false}
      size="full"
      title={header}
      bodyClassName="flex min-h-0 flex-col p-0 md:px-0 lg:flex-row lg:overflow-hidden"
    >
      {/* Locked: every field and switch below shows its value and takes no change. */}
      <FieldLockScope locked={locked}>
      {/* ── Left: Template Settings ───────────────────────────────────────────── */}
      <div className="shrink-0 border-b border-slate-200 bg-slate-50/50 lg:w-[260px] lg:overflow-y-auto lg:border-b-0 lg:border-r">
        <PanelHeading>Template Settings</PanelHeading>
        <div className="space-y-4 p-3">
          <DialogField
            label="Template Name"
            required
            htmlFor="print-template-name"
            state={form.name.trim() ? 'none' : 'error'}
            labelHint={form.name.trim() ? undefined : '— required'}
            badge={t.badge('name', 'Template Name')}
          >
            <Input
              id="print-template-name"
              value={form.name}
              onChange={(e) => t.patch({ name: e.target.value })}
              placeholder="e.g. Receipt: Surgery"
              maxLength={150}
              list="print-template-names"
              className={fieldClasses.input(form.name.trim() ? 'none' : 'error')}
            />
            <datalist id="print-template-names">
              {[...new Set(templates.map((x) => x.name.trim()).filter(Boolean))].sort().map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </DialogField>

          <SwitchRow
            id="print-template-logo"
            label={PRINT_HEADER_LABELS.logoDisplay}
            checked={form.logoDisplay}
            onChange={(on) => setFlag('logoDisplay', on)}
            badge={t.badge('logoDisplay', PRINT_HEADER_LABELS.logoDisplay)}
          />

          <DialogField
            label="Offered For"
            state={OFFERED_KEYS.some((k) => form[k]) ? 'none' : 'warning'}
            labelHint={OFFERED_KEYS.some((k) => form[k]) ? undefined : '— pick at least one'}
            description="Payment and credit sections only print on receipts."
          >
            <div className="space-y-1.5">
              {OFFERED_KEYS.map((key) => (
                <div key={key} className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2">
                  <Checkbox
                    id={`print-template-${key}`}
                    label={PRINT_HEADER_LABELS[key]}
                    checked={form[key]}
                    onChange={(e) => setFlag(key, e.target.checked)}
                    wrapperClassName="items-center gap-2"
                  />
                  {t.badge(key, PRINT_HEADER_LABELS[key])}
                </div>
              ))}
            </div>
          </DialogField>
        </div>
      </div>

      {/* ── Centre: the sections in print order ───────────────────────────────── */}
      <div className="bg-slate-100/60 p-6 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        <div className="mx-auto max-w-[680px] space-y-2">
          <p className="mb-3 text-center text-[11px] text-slate-500">
            {locked
              ? 'Sections in print order — click one to see what it shows. Cards render with sample data.'
              : 'Sections in print order — drag to reorder, click one to edit what it shows. Cards render with sample data.'}
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={form.sequence} strategy={verticalListSortingStrategy}>
              {form.sequence.map((key, index) => (
                <SectionCard
                  key={key}
                  section={PRINT_SECTION_BY_KEY[key]}
                  position={index + 1}
                  form={form}
                  disclosureName={disclosureName}
                  docType={docType}
                  selected={selectedKey === key}
                  modified={modifiedSections.has(key)}
                  dragDisabled={locked}
                  onSelect={() => setSelectedKey(key)}
                  onToggleDisplay={(on) => {
                    const displayKey = PRINT_SECTION_BY_KEY[key].displayKey;
                    if (displayKey) setFlag(displayKey, on);
                  }}
                  onRevert={() => revertSection(PRINT_SECTION_BY_KEY[key])}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {/* ── Right: Section Properties ─────────────────────────────────────────── */}
      <div className="shrink-0 border-t border-slate-200 bg-white lg:w-[320px] lg:overflow-y-auto lg:border-l lg:border-t-0">
        {selectedSection ? (
          <SectionProperties
            section={selectedSection}
            position={form.sequence.indexOf(selectedSection.orderKey) + 1}
            form={form}
            current={current}
            disclosures={disclosures}
            setFlag={setFlag}
            onDiscountDisplay={(v) => t.patch({ discountDisplayType: v })}
            onDisclosure={(id) => t.patch({ disclosureType: id })}
            badge={t.badge}
          />
        ) : (
          <div className="flex flex-col items-center justify-center p-6 text-center lg:h-full">
            <Settings className="mb-2 h-8 w-8 text-slate-300" aria-hidden />
            <p className="font-medium text-slate-400">Section Properties</p>
            <p className="mt-1 text-xs text-slate-500">
              {locked ? 'Select a section on the canvas to see what it prints' : 'Select a section on the canvas to choose what it prints'}
            </p>
          </div>
        )}
      </div>
      </FieldLockScope>

      {previewOpen && <PrintTemplatePreviewModal input={sheetInput} onClose={() => setPreviewOpen(false)} />}
      <ConfirmDialog
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={onClose}
        title="Discard changes?"
        message="You have unsaved changes. Close without saving them?"
        confirmLabel="Discard changes"
        variant="warning"
      />
    </Modal>
  );
}

// ─── Pieces ────────────────────────────────────────────────────────────────────

function PanelHeading({ children }: { children: ReactNode }) {
  return (
    <div className="border-b border-slate-200 px-3 py-2.5">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</p>
    </div>
  );
}

/** A count badge; hover (or focus) shows the list, as corp's header lists. */
function HoverList({ tone, label, heading, items }: { tone: 'error' | 'warning'; label: string; heading: string; items: string[] }) {
  const error = tone === 'error';
  return (
    <div className="group relative">
      <span
        tabIndex={0}
        className={cn(
          'inline-flex cursor-default items-center rounded-full border px-2 py-0.5 text-[11px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          error ? 'border-red-200 bg-red-600 text-white' : 'border-amber-200 bg-amber-100 text-amber-800',
        )}
      >
        {label}
      </span>
      <div
        role="tooltip"
        className={cn(
          'absolute left-0 top-full z-50 mt-1 hidden w-[calc(100vw-2rem)] max-w-[340px] rounded-md border p-3 shadow-lg group-focus-within:block group-hover:block',
          error ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50',
        )}
      >
        <p className={cn('mb-1 text-sm font-medium', error ? 'text-red-700' : 'text-amber-800')}>{heading}</p>
        <ul className={cn('list-disc space-y-0.5 pl-4 text-xs', error ? 'text-red-600' : 'text-amber-700')}>
          {items.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SwitchRow({
  id,
  label,
  checked,
  onChange,
  badge,
  disabled,
  hint,
  bordered = true,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
  badge?: ReactNode;
  disabled?: boolean;
  hint?: string;
  bordered?: boolean;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-3', bordered && 'rounded-md border border-slate-200 bg-white px-3 py-2')}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <label htmlFor={id} className={cn('text-sm', disabled ? 'text-slate-400' : 'cursor-pointer text-slate-900')}>
            {label}
          </label>
          {badge}
        </div>
        {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
      </div>
      <Switch id={id} checked={checked} onChange={onChange} disabled={disabled} aria-label={label} />
    </div>
  );
}

function SectionCard({
  section,
  position,
  form,
  disclosureName,
  docType,
  selected,
  modified,
  dragDisabled = false,
  onSelect,
  onToggleDisplay,
  onRevert,
}: {
  section: PrintSection;
  position: number;
  form: PrintTemplateForm;
  disclosureName: string;
  docType: ReturnType<typeof offeredDocTypes>[number];
  selected: boolean;
  modified: boolean;
  /** No drag and no grip (a Locked page): the card only opens its properties. */
  dragDisabled?: boolean;
  onSelect: () => void;
  onToggleDisplay: (on: boolean) => void;
  onRevert: () => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: section.orderKey,
    disabled: dragDisabled,
  });
  const hidden = section.displayKey ? !form[section.displayKey] : false;
  const receiptOnly = section.note !== undefined;
  // Receipts-only blocks are drawn as on a receipt (dimmed, with a note) so their content can be seen.
  const body = hidden
    ? null
    : renderPrintSection(section.orderKey, flagsOf(form), form.discountDisplayType, disclosureName, receiptOnly ? 'receipt' : docType);
  const notOnThisDoc = receiptOnly && docType !== 'receipt';

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`#${position} ${section.title}`}
      data-section={section.orderKey}
      onPointerDown={dragDisabled ? undefined : (listeners?.onPointerDown as PointerEventHandler<HTMLDivElement> | undefined)}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative cursor-pointer rounded-lg border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        selected
          ? 'border-green-500 bg-white shadow-md shadow-green-100'
          : modified
            ? 'border-blue-300 bg-blue-50/20'
            : 'border-slate-200 bg-white hover:border-slate-300',
        // Hidden sections read as muted (corp used a dashed border; Prospera keeps borders solid).
        hidden && !selected && 'bg-slate-50',
        isDragging && 'z-10 scale-[0.98] opacity-40',
      )}
    >
      <div className="flex items-start gap-2 px-3 py-2.5">
        {dragDisabled ? (
          // Holds the grip's place, so the cards line up as they do with it.
          <span className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <button
            ref={setActivatorNodeRef}
            type="button"
            {...attributes}
            onKeyDown={listeners?.onKeyDown as KeyboardEventHandler<HTMLButtonElement> | undefined}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Move ${section.title}`}
            className="mt-0.5 shrink-0 cursor-grab rounded text-slate-300 opacity-0 transition-opacity hover:text-slate-500 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 active:cursor-grabbing group-hover:opacity-100"
          >
            <GripVertical className="h-4 w-4" aria-hidden />
          </button>
        )}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-semibold tabular-nums text-slate-500">#{position}</span>
          <span className={cn('text-sm font-medium', hidden ? 'text-slate-400' : 'text-slate-800')}>{section.title}</span>
          {section.note && (
            <OutlineBadge tone="amber" className="h-4 px-1 py-0 text-[10px]">
              {section.note}
            </OutlineBadge>
          )}
          {hidden && <OutlineBadge className="h-4 px-1 py-0 text-[10px] text-slate-500">Hidden</OutlineBadge>}
          {modified && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-blue-700">
              modified
              <button
                type="button"
                className="hover:text-blue-900"
                title="Revert this section"
                aria-label={`Revert ${section.title}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onRevert();
                }}
              >
                <RotateCcw className="h-2.5 w-2.5" aria-hidden />
              </button>
            </span>
          )}
        </div>
        {section.displayKey && (
          <div className="flex shrink-0 items-center gap-1.5 text-slate-500" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
            {hidden ? <EyeOff className="h-3.5 w-3.5" aria-hidden /> : <Eye className="h-3.5 w-3.5" aria-hidden />}
            <Switch checked={!hidden} onChange={onToggleDisplay} aria-label={`Display ${section.title}`} />
          </div>
        )}
      </div>

      <div className={cn('pb-3 pl-9 pr-4 text-slate-900', notOnThisDoc && 'opacity-60')}>
        {hidden ? (
          <p className="text-xs italic text-slate-400">Hidden — this section will not print.</p>
        ) : body ? (
          <div className="rounded border border-slate-100 bg-slate-50/40 px-3 pb-2 [&_h3]:mt-2">{body}</div>
        ) : (
          <p className="text-xs italic text-slate-400">Nothing selected — this section prints empty.</p>
        )}
        {notOnThisDoc && !hidden && <p className="mt-1 text-[10px] text-amber-600">Only printed on receipts.</p>}
      </div>
    </div>
  );
}

function SectionProperties({
  section,
  position,
  form,
  current,
  disclosures,
  setFlag,
  onDiscountDisplay,
  onDisclosure,
  badge,
}: {
  section: PrintSection;
  position: number;
  form: PrintTemplateForm;
  current: PrintTemplate | null;
  disclosures: PrintDisclosure[];
  setFlag: (key: PrintBoolField, on: boolean) => void;
  onDiscountDisplay: (value: number) => void;
  onDisclosure: (id: number) => void;
  badge: (key: keyof PrintTemplateForm, label: string) => ReactNode;
}) {
  const hidden = section.displayKey ? !form[section.displayKey] : false;

  // Active disclosures, plus the saved one when it is no longer active (corp showed it blank).
  const disclosureOptions = [
    { value: 0, label: 'None' },
    ...disclosures.map((d) => ({ value: d.disclosuresID, label: d.name })),
  ];
  if (current?.disclosureType && !disclosures.some((d) => d.disclosuresID === current.disclosureType)) {
    disclosureOptions.push({ value: current.disclosureType, label: `${current.disclosureName ?? `Disclosure #${current.disclosureType}`} (inactive)` });
  }

  return (
    <>
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2.5">
        <span className="truncate text-sm font-semibold text-slate-900">{section.title}</span>
        <OutlineBadge className="ml-auto shrink-0 text-[10px]">#{position}</OutlineBadge>
      </div>
      <div className="space-y-4 p-4">
        {section.note && (
          <p className="flex items-center gap-1 text-[11px] text-amber-600">
            <AlertTriangle className="h-3 w-3" aria-hidden /> Prints on receipts only.
          </p>
        )}

        {section.displayKey && (
          <SwitchRow
            id={`print-section-display-${section.orderKey}`}
            label="Display section"
            checked={!hidden}
            onChange={(on) => setFlag(section.displayKey!, on)}
            badge={badge(section.displayKey, 'Display section')}
          />
        )}

        {section.fields.length > 0 && (
          <div className={cn('space-y-3', hidden && 'pointer-events-none opacity-50')} aria-disabled={hidden || undefined}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">What prints</p>
            {section.fields.map((field) => {
              if (field.kind === 'toggle') {
                const disabled = field.key === 'witnessSignature' && !form.signatureLine;
                return (
                  <SwitchRow
                    key={field.key}
                    id={`print-field-${field.key}`}
                    label={field.label}
                    checked={form[field.key]}
                    onChange={(on) => setFlag(field.key, on)}
                    badge={badge(field.key, field.label)}
                    disabled={disabled}
                    hint={disabled ? 'Needs the signature line' : undefined}
                    bordered={false}
                  />
                );
              }
              if (field.kind === 'discountDisplay') {
                return (
                  <div key="discountDisplay" className="space-y-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <label htmlFor="print-discount-display" className="text-sm text-slate-900">
                        {field.label}
                      </label>
                      {badge('discountDisplayType', field.label)}
                    </div>
                    <Select<number>
                      id="print-discount-display"
                      options={DISCOUNT_DISPLAY_OPTIONS}
                      value={form.discountDisplayType}
                      onChange={(v) => v != null && onDiscountDisplay(v)}
                      wrapperClassName="w-fit"
                    />
                  </div>
                );
              }
              return (
                <div key="disclosure" className="space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <label htmlFor="print-disclosure" className="text-sm text-slate-900">
                      {field.label}
                    </label>
                    {badge('disclosureType', field.label)}
                  </div>
                  <Select<number>
                    id="print-disclosure"
                    options={disclosureOptions}
                    value={form.disclosureType}
                    onChange={(v) => onDisclosure(v ?? 0)}
                    wrapperClassName="w-fit"
                  />
                  {disclosures.length === 0 && <p className={DESCRIPTION_CLASS}>This account has no active disclosures.</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
